import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { NotificationService } from '../../core/services/notification.service';
import { ApiService } from '../../core/services/api.service';
import { Language } from '../../core/models/api-response.model';
import { StateService } from '../../core/services/state.service';

interface NavLink {
  label: string;
  route: string;
  exact: boolean;
}

interface LanguageResponse {
  data: Record<string, Language[]>;
}

@Component({
  selector: 'app-header',
  imports: [RouterLink],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header {
  private readonly stateService = inject(StateService);
  private readonly notificationService = inject(NotificationService);
  private readonly apiService = inject(ApiService);
  private readonly router = inject(Router);

  /** Kept in sync with navigation so the ref-id badge can be hidden on excluded routes. */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly languages = signal<LanguageResponse | null>(null);
  protected readonly currentLanguage = signal(this.stateService.getLanguage());


  readonly navLinks: NavLink[] = [
    { label: 'Home', route: '/', exact: true },
  ];

  readonly notifications = this.notificationService.notifications;

  protected readonly langs = computed(() => this.languages()?.data ?? null);

  protected readonly languageList = computed<Language[]>(() =>
    Object.values(this.langs() ?? {}).flat(),
  );

  protected readonly currentLanguageName = computed(() => {
    const code = this.currentLanguage();
    return this.languageList().find((language) => language.lang_code === code)?.lang_name ?? code;
  });

  protected readonly isLangMenuOpen = signal(false);

  protected toggleLangMenu(): void {
    this.isLangMenuOpen.update((open) => !open);
  }

  protected selectLanguage(language: Language): void {
    this.stateService.setLanguage(language.lang_code);
    this.currentLanguage.set(language.lang_code);
    this.isLangMenuOpen.set(false);
  }

  /** Current configuration's ref_id, or null outside the configurator flow. */
  protected refId(): string | null {
    return this.stateService.refId;
  }

  /** The ref-id badge is hidden on the homepage and the start screen, shown everywhere else. */
  protected showRefBadge(): boolean {
    const path = this.currentUrl().split(/[?#]/)[0];
    return path !== '/' && path !== '/configuration-start';
  }

  protected dismissNotification(id: string): void {
    this.notificationService.dismiss(id);
  }

  protected async copyRefId(refId: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(refId);
      this.notificationService.success('Reference ID copied to clipboard.');
    } catch {
      this.notificationService.error('Could not copy the reference ID.');
    }
  }

  ngOnInit(): void {
    this.apiService
      .get<LanguageResponse>('', { params: { action: 'languages' } })
      .subscribe({
        next: (response) => {
          this.languages.set(response);
          console.log('Languages:', this.langs());
        },
        error: (error: Error) => this.notificationService.error(error.message),
      });
  }

}
